import React, { useMemo, useState, useEffect, useCallback } from "react";
import { MaterialReactTable } from "material-react-table";
import { 
  Box, 
  Button, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  TextField, 
  Select, 
  MenuItem, 
  FormControl, 
  InputLabel, 
  Checkbox, 
  FormControlLabel,
  IconButton,
  Tabs,
  Tab,
  Typography,
  Alert,
  CircularProgress,
  Snackbar
} from "@mui/material";
import { 
  Add, 
  Edit, 
  Delete, 
  GetApp, 
  Print, 
  Close,
} from "@mui/icons-material";
import ServiceFactory, { moduleConfigService, getDataSource } from '../services/ApiService'; 
import { fmtMoney, pillCls } from "../data/utils"; 

// Tab Panel Component
const TabPanel = ({ children, value, index, ...other }) => (
  <div role="tabpanel" hidden={value !== index} {...other}>
    {value === index && <Box sx={{ p: 2 }}>{children}</Box>}
  </div>
);

// Error Component
const ErrorAlert = ({ error, onRetry }) => (
  <Alert severity="error" sx={{ mt: 2 }} action={
    onRetry && (<Button color="inherit" size="small" onClick={onRetry}>Retry</Button>)
  }>
    {error}
  </Alert>
);

// Smart Select Component that fetches its own data
const DynamicSelect = ({ field, value, onChange }) => {
    const [options, setOptions] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const dataSourceUrl = field.properties?.dataSource;
        if (dataSourceUrl) {
            setLoading(true);
            getDataSource(dataSourceUrl)
                .then(result => {
                    if (result.success) {
                        setOptions(result.data || []);
                    }
                })
                .catch(err => console.error(`Failed to load data for ${field.label}:`, err))
                .finally(() => setLoading(false));
        } else {
            setOptions(field.options || []);
        }
    }, [field.properties?.dataSource, field.options, field.label]);

    return (
        <FormControl fullWidth size="small" required={field.required}>
            <InputLabel>{field.label}</InputLabel>
            <Select value={value || ''} label={field.label} onChange={onChange}>
                {loading && <MenuItem disabled>Loading...</MenuItem>}
                {options.map(option =>
                    typeof option === 'object'
                        ? <MenuItem key={option.id} value={option.id}>{option.name}</MenuItem>
                        : <MenuItem key={option} value={option}>{option}</MenuItem>
                )}
            </Select>
        </FormControl>
    );
};

// Generic Form Component
const GenericForm = ({ moduleConfig, initialData, open, onClose, onSave }) => {
    const [tabValue, setTabValue] = useState(0);
    const [formData, setFormData] = useState({});
    const [formLoading, setFormLoading] = useState(false);
    const [formError, setFormError] = useState('');
  
    useEffect(() => {
      if (open) {
        const initialFormData = { ...(initialData || {}) };
        moduleConfig?.formTabs?.flatMap(tab => tab.fields).forEach(field => {
          if (initialFormData[field.key] && Array.isArray(initialFormData[field.key])) {
            initialFormData[field.key] = initialFormData[field.key].join(', ');
          }
        });
        setFormData(initialFormData);
        setTabValue(0);
        setFormError('');
      }
    }, [open, initialData, moduleConfig]);
  
    const handleFieldChange = (key, value) => {
      setFormData(prev => ({ ...prev, [key]: value }));
    };
  
    const handleSubmit = async () => {
      if (formLoading) return;
      try {
        setFormLoading(true);
        setFormError('');
        const processedData = { ...formData };
        
        moduleConfig?.formTabs?.flatMap(tab => tab.fields).forEach(field => {
          const value = processedData[field.key];
          if ((field.key === 'tags' || field.key === 'values') && typeof value === 'string') {
            processedData[field.key] = value.split(',').map(t => t.trim()).filter(Boolean);
          }
          if (field.type === 'number' && value !== undefined && value !== '') {
            processedData[field.key] = Number(value);
          }
        });
        
        await onSave(processedData);
        
      } catch (error) {
        setFormError(error.message || 'Failed to save the record.');
      } finally {
        setFormLoading(false);
      }
    };
    
    const renderField = (field) => {
      const value = formData[field.key] === null || formData[field.key] === undefined ? '' : formData[field.key];
      
      switch (field.type) {
        case 'select':
          return (<DynamicSelect field={field} value={value} onChange={(e) => handleFieldChange(field.key, e.target.value)} />);
        case 'checkbox':
          return (<FormControlLabel control={<Checkbox checked={!!value} onChange={(e) => handleFieldChange(field.key, e.target.checked)} />} label={field.label}/>);
        case 'textarea':
          return (<TextField fullWidth multiline rows={3} size="small" label={field.label} value={value} onChange={(e) => handleFieldChange(field.key, e.target.value)} placeholder={field.placeholder} />);
        case 'date':
          const dateValue = value ? new Date(value).toISOString().split('T')[0] : '';
          return (<TextField fullWidth size="small" type="date" label={field.label} value={dateValue} onChange={(e) => handleFieldChange(field.key, e.target.value)} InputLabelProps={{ shrink: true }} />);
        default:
          return (<TextField fullWidth size="small" type={field.type || 'text'} label={field.label} value={value} required={field.required}   disabled={field.readOnly} onChange={(e) => handleFieldChange(field.key, e.target.value)} placeholder={field.placeholder} inputProps={field.type === 'number' ? { step: 'any' } : {}}/>);
      }
    };
  
    return (
      <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            {initialData ? 'Edit' : 'New'} {moduleConfig?.title?.slice(0, -1) || 'Record'}
            <IconButton onClick={onClose} size="small" disabled={formLoading}><Close /></IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {formError && (<Alert severity="error" sx={{ mb: 2 }}>{formError}</Alert>)}
          {moduleConfig?.formTabs && moduleConfig.formTabs.length > 1 ? (
            <>
              <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} sx={{ mb: 2 }}>
                {moduleConfig.formTabs.map((tab, index) => (<Tab key={index} label={tab.name} />))}
              </Tabs>
              {moduleConfig.formTabs.map((tab, index) => (
                <TabPanel key={index} value={tabValue} index={index}>
                  <Box display="grid" gridTemplateColumns="repeat(12, 1fr)" gap={2}>
                    {tab.fields.map(field => (<Box key={field.key} gridColumn={`span ${field.colSpan || 6}`}>{renderField(field)}</Box>))}
                  </Box>
                </TabPanel>
              ))}
            </>
          ) : (
            <Box display="grid" gridTemplateColumns="repeat(12, 1fr)" gap={2}>
              {(moduleConfig?.formTabs?.[0]?.fields || []).map(field => (<Box key={field.key} gridColumn={`span ${field.colSpan || 6}`}>{renderField(field)}</Box>))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={formLoading}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" disabled={formLoading} startIcon={formLoading ? <CircularProgress size={16} color="inherit" /> : null}>
            {formLoading ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    );
};


const ErpTablePage = ({ moduleKey }) => {
  const [data, setData] = useState([]);
  const [moduleConfig, setModuleConfig] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [editingData, setEditingData] = useState(null);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 25 });
  const [totalCount, setTotalCount] = useState(0);
  const [globalFilter, setGlobalFilter] = useState('');
  const [sorting, setSorting] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const loadData = useCallback(async () => {
    if (!moduleConfig) return;
    setIsLoading(true);
    
    const service = ServiceFactory.getService(moduleConfig.controllerRoute);
    const params = {
      page: pagination.pageIndex + 1,
      pageSize: pagination.pageSize,
      search: globalFilter,
      sortBy: sorting[0]?.id || '',
      sortDirection: sorting[0]?.desc ? 'desc' : 'asc',
    };

    try {
      const response = await service.getAll(params);
      if (response.success) {
        setData(response.data || []);
        setTotalCount(response.totalCount || 0);
      } else {
        setError(response.message || 'Failed to load data');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [moduleConfig, pagination, globalFilter, sorting]);

  const loadConfigAndData = useCallback(async () => {
      setIsLoading(true);
      setError('');
      try {
          const configResponse = await moduleConfigService.getModuleConfiguration(moduleKey);
          if (configResponse.success && configResponse.data) {
              setModuleConfig(configResponse.data);
              // Now that config is set, loadData will be triggered by the next useEffect
          } else {
              setError(configResponse.message || `Module '${moduleKey}' not found.`);
              setIsLoading(false);
          }
      } catch (err) {
          setError(err.message);
          setIsLoading(false);
      }
  }, [moduleKey]);

  useEffect(() => {
    loadConfigAndData();
  }, [loadConfigAndData]);
  
  useEffect(() => {
    if (moduleConfig) {
      loadData();
    }
  }, [moduleConfig, loadData]);

  const columns = useMemo(() => {
    if (!moduleConfig?.columns) return [];
    
    return [
      ...moduleConfig.columns.map(col => ({
        accessorKey: col.key,
        header: col.header,
        size: col.size,
        Cell: ({ cell }) => {
          const value = cell.getValue();
          if (value === null || value === undefined) return '';
          if (col.dataType === 'boolean') return value ? 'Yes' : 'No';
          if (col.dataType === 'currency') return fmtMoney(value);
          if (Array.isArray(value)) return value.join(', ');
          if (col.header?.toLowerCase().includes('status')) return <span className={pillCls(value)}>{value}</span>;
          return String(value);
        }
      })),
      {
        accessorKey: 'actions',
        header: 'Actions',
        size: 120,
        enableSorting: false,
        enableColumnFilter: false,
        Cell: ({ row }) => (
          <Box display="flex" gap={0.5}>
            <IconButton size="small" onClick={() => handleEdit(row.original)} title="Edit"><Edit fontSize="small" /></IconButton>
            <IconButton size="small" color="error" onClick={() => handleDelete(row.original)} title="Delete"><Delete fontSize="small" /></IconButton>
          </Box>
        ),
      },
    ];
  }, [moduleConfig]);

const handleAdd = async () => {
    // Show a loading indicator if you want
    // setIsLoading(true); 

    try {
        const service = ServiceFactory.getService(moduleConfig.controllerRoute);
        const response = await service.getNextNumber(1); // Assuming companyId = 1
console.log("response",response)
        if (response.success) {
            const newCode = response.data;
            
            // Create an initial record with the new code pre-filled
            const initialRecord = {
                [moduleConfig.columns[0].key]: newCode // Sets 'assetCode' to the new code
            };

            // Open the form with the pre-filled, read-only field
            setEditingData(initialRecord);
            setFormOpen(true);
        } else {
            // Handle error if you can't get a new code
            setSnackbar({ open: true, message: response.message || 'Could not generate a new code.', severity: 'error' });
        }
    } catch (error) {
        setSnackbar({ open: true, message: error.message, severity: 'error' });
    } finally {
        // setIsLoading(false);
    }
};

  const handleEdit = (rowData) => {
    setEditingData(rowData);
    setFormOpen(true);
  };

  const handleDelete = async (rowData) => {
    if (!window.confirm('Are you sure you want to delete this record?')) return;
    try {
      const service = ServiceFactory.getService(moduleConfig.controllerRoute);
      const response = await service.delete(rowData.id);
      if (response.success) {
        setSnackbar({ open: true, message: 'Record deleted successfully', severity: 'success' });
        loadData();
      } else {
        throw new Error(response.message);
      }
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    }
  };

  const handleSave = async (formData) => {
      const service = ServiceFactory.getService(moduleConfig.controllerRoute);
      let response;
      console.log("data", JSON.stringify(formData))
         console.log("editing",editingData)
      if (editingData.id) {
        response = await service.update(editingData.id, formData);
      } else {
        response = await service.create(formData);
      }

      if (response.success) {
        setSnackbar({ open: true, message: `Record ${editingData ? 'updated' : 'created'} successfully`, severity: 'success' });
        setFormOpen(false);
        setEditingData(null);
        loadData();
      } else {
        throw new Error(response.message || 'Failed to save record');
      }
  };

  const handleRefresh = () => loadConfigAndData();
  const handleCloseSnackbar = () => setSnackbar(prev => ({ ...prev, open: false }));

  if (isLoading && !moduleConfig) {
    return <Box p={3} display="flex" justifyContent="center"><CircularProgress /></Box>;
  }
  
  if (error) {
    return <Box p={3}><ErrorAlert error={error} onRetry={handleRefresh} /></Box>;
  }
  
  if (!moduleConfig) {
    return <Box p={3}><Typography>Loading configuration...</Typography></Box>;
  }

  return (
    <div className="content">
      <div className="page-header d-flex mb-3">
        <h2 className="page-heading fs-7">{moduleConfig.title}</h2>
      </div>
      <div className="card">
        <div className="card-body">
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Box>
              <Typography variant="h5">{moduleConfig.title}</Typography>
              <Typography variant="body2" color="text.secondary">{moduleConfig.subtitle}</Typography>
            </Box>
            <Box display="flex" gap={1}>
              <Button onClick={() => {}} startIcon={<GetApp />} variant="outlined" size="small">Export CSV</Button>
              <Button onClick={() => window.print()} startIcon={<Print />} variant="outlined" size="small">Print</Button>
              {moduleConfig.formTabs && (<Button onClick={handleAdd} variant="contained" startIcon={<Add />} size="small">Add New</Button>)}
            </Box>
          </Box>

          <MaterialReactTable
            columns={columns}
            data={data}
            manualPagination
            manualSorting
            manualFiltering
            rowCount={totalCount}
            state={{pagination, globalFilter, sorting }}
            onPaginationChange={setPagination}
            onGlobalFilterChange={setGlobalFilter}
            onSortingChange={setSorting}
            initialState={{ density: "compact" }}
            muiToolbarAlertBannerProps={error ? { color: 'error', children: error } : undefined}
          />
        </div>
      </div>

      {formOpen && (
          <GenericForm
            moduleConfig={moduleConfig}
            initialData={editingData}
            open={formOpen}
            onClose={() => { setFormOpen(false); setEditingData(null); }}
            onSave={handleSave}
          />
      )}

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default ErpTablePage;